import { useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import { Shield, LogIn, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();

  // Get error from URL params (set by backend on OAuth failure)
  const urlError = searchParams.get('error');

  // Get the redirect path from location state, or default to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Clear error when component mounts
  useEffect(() => {
    if (error) clearError();
  }, []);

  const handleLogin = () => {
    login();
  };

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const displayError = urlError || error;

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-400/5 via-transparent to-transparent" />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <Shield className="h-12 w-12 text-red-400" />
          <span className="text-3xl font-bold text-white">TARK Admin</span>
        </div>

        <Card variant="glass">
          <CardHeader className="text-center">
            <CardTitle>Admin Portal</CardTitle>
            <p className="text-sm text-slate-400 mt-2">
              Sign in to access the admin dashboard
            </p>
          </CardHeader>

          <CardContent>
            {/* Error display */}
            {displayError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{getErrorMessage(displayError)}</span>
              </div>
            )}

            {/* Login Button */}
            <Button
              onClick={handleLogin}
              className="w-full"
              size="lg"
              rightIcon={<LogIn className="h-4 w-4" />}
            >
              Sign in with Cognito
            </Button>

            {/* Info */}
            <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-2">
                Secure Authentication
              </h4>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• AWS Cognito authentication</li>
                <li>• Server-side session management</li>
                <li>• HttpOnly cookie for security</li>
              </ul>
            </div>

            {/* Environment indicator */}
            <div className="mt-6 pt-4 border-t border-slate-700">
              <p className="text-xs text-slate-500 text-center">
                Environment: <span className="text-blue-400 font-mono">{getEnvironment()}</span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-slate-600 mt-6">
          Protected by TARK Security
        </p>
      </div>
    </div>
  );
}

function getEnvironment(): string {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'LOCAL';
  }
  if (hostname.includes('dev.') || hostname.includes('-dev')) {
    return 'DEV';
  }
  if (hostname.includes('staging.') || hostname.includes('-staging')) {
    return 'STAGING';
  }
  return 'PROD';
}

function getErrorMessage(error: string): string {
  switch (error) {
    case 'no_code':
      return 'Authentication failed: No authorization code received';
    case 'token_exchange_failed':
      return 'Authentication failed: Could not exchange code for tokens';
    case 'invalid_token':
      return 'Authentication failed: Invalid token received';
    case 'callback_failed':
      return 'Authentication failed: Callback processing error';
    case 'access_denied':
      return 'Access denied. Please contact your administrator.';
    default:
      return `Authentication error: ${error}`;
  }
}
